import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import {
  CallState,
  ExperimentState,
  ExperimentStateWithCallState,
  LaunchedApplicationProperties,
} from "redmond";
import { CONTROL, ExperimentsContext } from "@capone/experiments";
import { fetchActiveExperiments } from "../api/v1/experiments/fetchExperiments";

// Export the methods that used to live here to avoid a larger refactor
export {
  AVAILABLE,
  CONTROL,
  VARIANT_1,
  VARIANT_2,
  DEFAULT_VARIANTS,
  type DefaultVariantType,
  getExperimentVariantCustomVariants,
  getExperimentVariant,
  getDefaultExperimentVariant,
  useExperimentsContext as useExperiments,
} from "@capone/experiments";

// Active Experiments
export const SEAT_SELECTION = "c1-marketplace-seatselection";
export const MAINTENANCE = "c1-marketplace-maintenancepage";
export const FLIGHTS_MAINTENANCE = "c1-marketplace-flights-maintenancepage";
export const HOTELS_MAINTENANCE = "c1-marketplace-hotels-maintenancepage";
export const CARS_MAINTENANCE = "c1-marketplace-cars-maintenancepage";
export const CARS_LOYALTY_SECTION = "cars-hertz-loyalty-section";
export const TRIPS_MAINTENANCE = "c1-marketplace-trips-maintenancepage";
export const GOVERNMENT_SHUTDOWN_BANNER_TRIPS =
  "c1-fintech-disruption-government-shutdown-banner";
export const PACKAGES_MAINTENANCE = "c1-marketplace-packages-maintenancepage";
export const ACTIVITIES_MAINTENANCE = "c1-activities-maintenancepage";
export const TRAVEL_WALLET_OFFER_EXPERIMENT =
  "c1-marketplace-travel-wallet-offers";
export const TRAVEL_WALLET_CREDIT_EXPERIMENT =
  "c1-marketplace-travel-wallet-credits";
export const CASH_VALUEPROP_EXPERIMENT = `c1-marketplace-cash-valueprop-merch`;
export const PREMIER_COLLECTION_EXPERIMENT =
  "c1-marketplace-luxury-hotels-collection";
export const HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT =
  "c1-marketplace-homepage-hotel-cross-sell";
export const CREDIT_OFFER_STACKING_V1 =
  "c1-marketplace-credit-offer-stacking-v1";
export const TRAVEL_CREDIT_HISTORY_EXPERIMENT =
  "c1-marketplace-credit-transaction-history";
export const PREFERRED_HOTEL_EXPERIMENT =
  "c1-marketplace-cot-preferred-hotel-merch";
export const RECENTLY_VIEWED_MASTER_V1 = "c1-marketplace-recently-viewed_v1";
export const RECENTLY_VIEWED_DESKTOP_P0_EXPERIMENT =
  "c1-marketplace-recently-viewed-desktop-p0";
export const RECENTLY_VIEWED_MOBILE_P0_EXPERIMENT =
  "c1-marketplace-recently-viewed-mobile-p0";
export const RECENTLY_VIEWED_V2_FLIGHTS =
  "c1-marketplace-recently-viewed-v2-air";
export const RECENTLY_VIEWED_V2_CARS = "c1-marketplace-recently-viewed-v2-cars";
export const ANNUAL_TRAVEL_CREDITS = "c1-marketplace-annual-travel-credits";
export const CFAR_DISCOUNT = "c1-cfar-air-discount";
export const TRAVEL_SALE = "c1-marketplace-travel-sale";
export const VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT =
  "c1-marketplace-vr-premium-cards";
export const VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT =
  "c1-marketplace-vr-non-premium-cards";
export const POST_BOOKING_OFFER_EXPERIMENT =
  "c1-fintech-disruption-air-postbookingoffer";
export const POST_BOOKING_OFFER_EXPERIMENT_REWARDS =
  "c1-fintech-disruption-air-postbookingoffer-rewards";
export const POST_BOOKING_OFFER_MY_TRIPS =
  "c1-fintech-disruption-air-postbooking-permanent-mytrips";
export const CUSTOMER_PROFILE_EXPERIMENT = "c1-marketplace-customer-profile";
export const COACHELLA_OFFER_EXPERIMENT = "c1-marketplace-coachella-offer";
export const RESTRICT_FUNNELS = "c1-agency-restrict-funnels";
export const GLOBAL_MOBILE_NAV_EXPERIMENT = "c1-marketplace-global-mobile-nav";
export const HOTEL_CROSS_SELL_V3 = "c1-marketplace-hotel-cross-sell-v3";
export const PACKAGES_EXPERIMENT = "c1-marketplace-packages";
export const LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH =
  "c1-luxury-vacation-rental-funnel-launch";
export const STAYS_HOMEPAGE = "c1-stays-homepage";
export const STAYS_VACATION_RENTALS_AWARENESS_MODAL =
  "c1-stays-vacation-rentals-awareness-modal";
export const EXPERIENCES_EXPERIMENT = "c1-agency-experiences";
export const CRUISES_EXPERIMENT = "c1-cruises-poc";
export const CACHE_HOTEL_TOKEN = "c1-marketplace-cache-hotel-token";
export const LC_GATING_EXPERIMENT =
  "c1-marketplace-lifestyle-hotels-collection";
export const CORP_SPARK_MIGRATION_BANNER =
  "c1-corporate-spark-migration-banner";
export const CORP_VXB_MIGRATION_TAKEOVER_FORCE_SHOW =
  "c1-corporate-vxb-migration-takeover-force-show";
export const CORP_VXB_MIGRATION_TAKEOVER =
  "c1-corporate-vxb-migration-takeover";
export const CORP_MY_PROFILE_PAGE = "corp-my-profile-page";
export const CORP_SPEND_AND_GET_OFFER = "corp-spend-and-get-offer";
export const CORP_MYTRIPS_REPLATFORMING = "corp-mytrips-replatforming";
export const CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS =
  "corp-ftb-offer-additional-placements";
export const XMOD_V1_EXPERIMENT = "c1-marketplace-xmod-v1";
export const TRAVEL_WALLET_V2_REFRESH = "c1-travel-wallet-v2-refresh";
export const CONTINUE_YOUR_SEARCH_PACKAGES_REDESIGN =
  "continue-your-search-packages-redesign";
export const TRAVEL_APP_GTM_EXPERIMENT = "c1-portex-app-gtm";
export const COMBINED_SEARCH_EXPERIMENT = "c1-activities-combined-search";

// CORE
export const CORE_FLIGHTS = "c1-replatform-flights-funnel";
export const CORE_FLIGHTS_MULTICITY = "c1-replatform-flights-multicity";
export const CORE_ACTIVITIES = "c1-replatform-activities-funnel";
export const CORE_PACKAGES = "c1-replatform-packages-funnel";
export const CORE_PREMIUM_STAYS = "c1-replatform-premium-stays-funnel";
export const CORE_LUXURY_HOTELS_AVAILABILITY = "c1-replatform-luxury-hotels-availability";
export const CORE_LUXURY_HOTELS_SHOP = "c1-replatform-luxury-hotels-shop";
export const CORE_VACATION_RENTALS_SHOP = "c1-replatform-vacation-rentals-shop";
export const CORE_LUXURY_VACATION_RENTALS_SHOP =
  "c1-replatform-luxury-vacation-rentals-shop";
export const CORE_LUXURY_MOBILE_HOMEPAGE_SEARCH =
  "c1-replatform-luxury-mobile-homepage-search";
export const CORE_HOMEPAGE = "c1-homepage-redesign";

// variants for c1-cfar-air-discount
export const CFAR_DISCOUNT_CONTROL = "control";
export const CFAR_DISCOUNT_10_PERCENT = "10percentdiscount";
export const CFAR_DISCOUNT_25_PERCENT = "25percentdiscount";
export const CFAR_DISCOUNT_75_PERCENT = "75percentdiscount";
export const CFAR_DISCOUNT_100_PERCENT = "100percentdiscount";
export const CFAR_DISCOUNT_VARIANTS = [
  CFAR_DISCOUNT_CONTROL,
  CFAR_DISCOUNT_10_PERCENT,
  CFAR_DISCOUNT_25_PERCENT,
  CFAR_DISCOUNT_75_PERCENT,
  CFAR_DISCOUNT_100_PERCENT,
] as const;
export type CfarDiscountVariantType =
  | typeof CFAR_DISCOUNT_CONTROL
  | typeof CFAR_DISCOUNT_10_PERCENT
  | typeof CFAR_DISCOUNT_25_PERCENT
  | typeof CFAR_DISCOUNT_75_PERCENT
  | typeof CFAR_DISCOUNT_100_PERCENT;
// Variants for c1-marketplace-cash-valueprop-merch
export const CASH_VALUEPROP_A = "cash-valueprop-a";
export const CASH_VALUEPROP_B = "cash-valueprop-b";
export const CASH_VALUEPROP_C = "cash-valueprop-c";
export const CASH_VALUEPROP_VARIANTS = [
  CONTROL,
  CASH_VALUEPROP_A,
  CASH_VALUEPROP_B,
  CASH_VALUEPROP_C,
] as const;
export type CashValuePropVariantType =
  | typeof CONTROL
  | typeof CASH_VALUEPROP_A
  | typeof CASH_VALUEPROP_B
  | typeof CASH_VALUEPROP_C;

export const RECENTLY_VIEWED_DESKTOP_P0_V1 = "variant-1";
export const RECENTLY_VIEWED_DESKTOP_P0_V2 = "variant-2";
export const RECENTLY_VIEWED_DESKTOP_P0_V3 = "variant-3";
export const RECENTLY_VIEWED_DESKTOP_P0_V4 = "variant-4";
export const RECENTLY_VIEWED_DESKTOP_P0_VARIANTS = [
  CONTROL,
  RECENTLY_VIEWED_DESKTOP_P0_V1,
  RECENTLY_VIEWED_DESKTOP_P0_V2,
  RECENTLY_VIEWED_DESKTOP_P0_V3,
  RECENTLY_VIEWED_DESKTOP_P0_V4,
] as const;

export type RecentlyViewedDesktopP0PropVariantType =
  | typeof CONTROL
  | typeof RECENTLY_VIEWED_DESKTOP_P0_V1
  | typeof RECENTLY_VIEWED_DESKTOP_P0_V2
  | typeof RECENTLY_VIEWED_DESKTOP_P0_V3
  | typeof RECENTLY_VIEWED_DESKTOP_P0_V4;

// Variants for c1-marketplace-cot-preferred-hotel-merch
export const PREFERRED_HOTELS_V1 = "v1-special-rates";
export const PREFERRED_HOTELS_VARIANT = [CONTROL, PREFERRED_HOTELS_V1] as const;
export type PreferredHotelsPropVariantType =
  | typeof CONTROL
  | typeof PREFERRED_HOTELS_V1;
export const RECENTLY_VIEWED_MOBILE_P0_V1 = "variant-1";
export const RECENTLY_VIEWED_MOBILE_P0_V2 = "variant-2";
export const RECENTLY_VIEWED_MOBILE_P0_V3 = "variant-3";
export const RECENTLY_VIEWED_MOBILE_P0_VARIANTS = [
  CONTROL,
  RECENTLY_VIEWED_MOBILE_P0_V1,
  RECENTLY_VIEWED_MOBILE_P0_V2,
  RECENTLY_VIEWED_MOBILE_P0_V3,
] as const;

export type RecentlyViewedMobileP0PropVariantType =
  | typeof CONTROL
  | typeof RECENTLY_VIEWED_MOBILE_P0_V1
  | typeof RECENTLY_VIEWED_MOBILE_P0_V2
  | typeof RECENTLY_VIEWED_MOBILE_P0_V3;

// Variants for c1-marketplace-travel-sale
export const TRAVEL_SALE_LEAD_UP = "lead-up";
export const TRAVEL_SALE_ACTIVE = "active";
export const TRAVEL_SALE_VARIANTS = [
  CONTROL,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_ACTIVE,
] as const;
export type TravelSalePropVariantType =
  | typeof CONTROL
  | typeof TRAVEL_SALE_LEAD_UP
  | typeof TRAVEL_SALE_ACTIVE;

// Variants for c1-marketplace-customer-profile
export const DEFAULT_OFF = "default-off";
export const DEFAULT_ON = "default-on";
export const CUSTOMER_PROFILE_VARIANTS = [
  CONTROL,
  DEFAULT_OFF,
  DEFAULT_ON,
] as const;
export type CustomerProfileVariantType =
  | typeof CONTROL
  | typeof DEFAULT_OFF
  | typeof DEFAULT_ON;

// Variants for c1-marketplace-hotel-cross-sell-v3
export const DOLLAR_OFFER = "dollar-offer";
export const PERCENTAGE_OFFER = "percentage-offer";
export const HOTEL_CROSS_SELL_V3_VARIANTS = [
  CONTROL,
  DOLLAR_OFFER,
  PERCENTAGE_OFFER,
] as const;
export type HotelCrossSellV3VariantTYpe =
  | typeof CONTROL
  | typeof DOLLAR_OFFER
  | typeof PERCENTAGE_OFFER;

// Variants for c1-agency-restrict-funnels
export const CARS_ONLY = "cars-only";
export const FLIGHTS_ONLY = "flights-only";
export const HOTELS_ONLY = "hotels-only";
export const PREMIUM_STAYS_ONLY = "premium-stays-only";
export const RESTRICT_FUNNELS_VARIANTS = [
  CONTROL,
  CARS_ONLY,
  FLIGHTS_ONLY,
  HOTELS_ONLY,
  PREMIUM_STAYS_ONLY,
] as const;
export type RestrictFunnelsVariantType =
  | typeof CONTROL
  | typeof CARS_ONLY
  | typeof FLIGHTS_ONLY
  | typeof HOTELS_ONLY
  | typeof PREMIUM_STAYS_ONLY;

// Variants for c1-marketplace-packages
export const SELF_FUNDED = "self-funded";
export const NOT_SELF_FUNDED = "not-self-funded";
export const PACKAGES_EXPERIMENT_VARIANTS = [
  CONTROL,
  SELF_FUNDED,
  NOT_SELF_FUNDED,
] as const;
export type PackagesExperimentVariantType =
  | typeof CONTROL
  | typeof SELF_FUNDED
  | typeof NOT_SELF_FUNDED;

const defaultInitializer = (): ExperimentStateWithCallState => ({
  experiments: [],
  trackingProperties: undefined,
  callState: CallState.NotCalled,
});

export function addTrackingProperties(
  trackingProperties: Map<string, string> | undefined,
  properties: LaunchedApplicationProperties
): LaunchedApplicationProperties {
  if (trackingProperties) {
    properties["experiments"] = Object.keys(trackingProperties).map(
      (ex) => `${ex}_${trackingProperties![ex]}`
    );
  }
  return properties;
}

type ExperimentsProviderProps = {
  initState?: ExperimentState;
  isLoggedIn?: boolean;
  onStateChange?: (state: ExperimentStateWithCallState) => void;
};

const ExperimentsProvider: FC<
  ExperimentsProviderProps &
    PropsWithChildren<{ initState?: ExperimentStateWithCallState }>
> = ({
  initState = defaultInitializer(),
  isLoggedIn,
  children,
  onStateChange,
}) => {
  const [state, setState] = useState(initState);

  useEffect(() => {
    if (!initState?.experiments.length && isLoggedIn) {
      const fetchExperiments = async () => {
        setState({ ...state, callState: CallState.InProcess });
        await fetchActiveExperiments().then((result) => {
          setState({
            ...(result as ExperimentState),
            callState: CallState.Success,
          });
        });
      };
      fetchExperiments();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);

  return (
    <ExperimentsContext.Provider value={state}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export default ExperimentsProvider;
